import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { WebexTokenEnum } from '../../../../ms-teams-sdk/service/MsTeamsAuthenticationService';
import { getLocalStorageItem, setLocalStorageItem } from '../../../logger/src/LocalStorage';
import { Logger } from '../../../logger/src/Logger';
import { PresenceManager, UIPresenceData } from '../services/PresenceManager';
import { whenDeviceRegistered, whenWebexReady } from '../services/WebexInstanceService';
import { emitPresenceStackInitialized } from '../utils/presenceMetrics';
import { PresenceData } from '../utils/presenceUtils';

/**
 * Presence context value
 */
interface PresenceContextValue {
  /**
   * PresenceManager instance (null if not initialized)
   */
  manager: PresenceManager | null;
  /**
   * Presence data map (for React re-renders)
   */
  presenceMap: Map<string, PresenceData>;
  /**
   * Version counter (increments when map updates, forces re-renders)
   */
  version: number;
  /**
   * Get presence data for a user ID (convenience method)
   */
  getPresence: (userId: string) => UIPresenceData | undefined;
}

/**
 * Presence context
 */
const PresenceContext = createContext<PresenceContextValue>({
  manager: null,
  presenceMap: new Map(),
  version: 0,
  getPresence: () => undefined,
});

/**
 * Presence provider props
 * @param getWebex - Optional function to resolve the Webex instance. When provided, used instead of global window access for testability. When not provided, uses whenWebexReady().
 */
interface PresenceProviderProps {
  children: React.ReactNode;
  /** Optional: inject Webex instance resolver for testing. If not provided, uses whenWebexReady(). */
  getWebex?: () => Promise<any>;
}

/**
 * Presence Provider
 *
 * Initializes PresenceManager and provides it via Context to all child components.
 * Manages presence data state and notifies subscribers when data changes.
 * Accepts optional getWebex() for dependency injection (e.g. in tests pass a mock).
 */
export const PresenceProvider: React.FC<PresenceProviderProps> = ({ children, getWebex: getWebexProp }) => {
  const [manager, setManager] = useState<PresenceManager | null>(null);
  const [presenceMap, setPresenceMap] = useState<Map<string, PresenceData>>(new Map());
  const [version, setVersion] = useState(0);
  const [isInitializing, setIsInitializing] = useState(false);

  // Refs so cleanup can always access current manager and unsubscribe (effect closure has stale state)
  const managerRef = useRef<PresenceManager | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const retryCountRef = useRef(0);

  const MAX_INIT_RETRIES = 15;

  // Initialize manager when Webex is ready
  useEffect(() => {
    let isMounted = true;
    let retryTimeout: NodeJS.Timeout | null = null;
    let initializing = false;

    const initManager = async () => {
      if (initializing || managerRef.current) {
        return;
      }

      initializing = true;

      try {
        // Resolve Webex: use injected getter if provided (for tests), otherwise whenWebexReady (no direct window access)
        const webex = getWebexProp
          ? await getWebexProp()
          : await whenWebexReady(30000);

        if (!isMounted) return;

        if (!webex) {
          if (retryCountRef.current >= MAX_INIT_RETRIES) {
            Logger.info('[PresenceProvider] Webex not available. Max retries reached, giving up.');
            initializing = false;
            return;
          }
          retryCountRef.current += 1;
          Logger.info(`[PresenceProvider] Webex not available. Retrying (${retryCountRef.current}/${MAX_INIT_RETRIES})...`);
          retryTimeout = setTimeout(initManager, 2000);
          initializing = false;
          return;
        }

        // Read orgId from localStorage using the same helper as MsTeamsAuthenticationService
        let orgId = getLocalStorageItem<string>(WebexTokenEnum.ORG_ID);

        // Fallback: extract from webex and save
        if (!orgId) {
          orgId =
            webex?.internal?.device?.registered?.organizationId ||
            webex?.config?.orgId ||
            webex?.credentials?.orgId ||
            null;

          if (orgId) {
            setLocalStorageItem(WebexTokenEnum.ORG_ID, orgId);
            Logger.info(`[PresenceProvider] Saved orgId: ${orgId}`);
          }
        }

        if (!orgId) {
          if (retryCountRef.current >= MAX_INIT_RETRIES) {
            Logger.info('[PresenceProvider] OrgId not ready. Max retries reached, giving up.');
            initializing = false;
            return;
          }
          retryCountRef.current += 1;
          Logger.info(`[PresenceProvider] OrgId not ready. Retrying (${retryCountRef.current}/${MAX_INIT_RETRIES})...`);
          retryTimeout = setTimeout(initManager, 1000);
          initializing = false;
          return;
        }

        // Same readiness as PresenceManager.initialize: feature catalog is reliable after device registration.
        // Directory search does not need this because it runs on user action (later).
        try {
          await whenDeviceRegistered(20000);
        } catch (deviceErr) {
          Logger.error(
            `[PresenceProvider] Device not registered before presence init: ${deviceErr instanceof Error ? deviceErr.message : deviceErr}`
          );
          if (retryCountRef.current < MAX_INIT_RETRIES) {
            retryCountRef.current += 1;
            retryTimeout = setTimeout(initManager, 2000);
          }
          initializing = false;
          return;
        }


        const newManager = PresenceManager.obtain(webex);
        await newManager.initialize();

        if (!isMounted) return;

        const unsubscribe = newManager.subscribe((updatedMap) => {
          if (!isMounted) return;

          setPresenceMap(new Map(updatedMap));
          setVersion((v) => v + 1);
        });

        managerRef.current = newManager;
        unsubscribeRef.current = unsubscribe;
        setManager(newManager);
        retryCountRef.current = 0;

        try {
          emitPresenceStackInitialized();
        } catch (e) {
          Logger.error(
            `[PresenceProvider] emitPresenceStackInitialized failed: ${e instanceof Error ? e.message : String(e)}`
          );
        }
        Logger.info('[PresenceProvider] ✓ PresenceManager initialized');
      } catch (error) {
        Logger.error(`[PresenceProvider] Init failed: ${error}`);
        if (retryCountRef.current < MAX_INIT_RETRIES) {
          retryCountRef.current += 1;
          retryTimeout = setTimeout(initManager, 2000);
        }
      } finally {
        initializing = false;
      }
    };

    initManager();

    return () => {
      isMounted = false;
      if (retryTimeout) clearTimeout(retryTimeout);
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      if (managerRef.current) {
        managerRef.current.destroy();
        managerRef.current = null;
      }
    };
  }, [getWebexProp]);

  // Convenience method to get presence data
  const getPresence = useCallback((userId: string): UIPresenceData | undefined => {
    if (!manager) {
      return undefined;
    }
    return manager.getPresence(userId);
  }, [manager, presenceMap, version]); // Include version to force re-evaluation

  // Context value (memoized to prevent unnecessary re-renders)
  const contextValue = useMemo<PresenceContextValue>(() => ({
    manager,
    presenceMap,
    version,
    getPresence,
  }), [manager, presenceMap, version, getPresence]);

  return (
    <PresenceContext.Provider value={contextValue}>
      {children}
    </PresenceContext.Provider>
  );
};

/**
 * Hook to access presence context
 * 
 * @returns Presence context value
 */
export const usePresenceContext = (): PresenceContextValue => {
  const context = useContext(PresenceContext);
  if (!context) {
    // This should never happen if PresenceProvider is used correctly
    Logger.error('[usePresenceContext] Context not found. Make sure PresenceProvider is in the component tree.');
    return {
      manager: null,
      presenceMap: new Map(),
      version: 0,
      getPresence: () => undefined,
    };
  }
  return context;
};

